import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Backdrop,
  Button,
  DevNote,
  Drawer,
  DropdownMenu,
  FormFieldSet,
  InfoCardNewAdv,
  InfoTimeCardAdv,
  InfoWrapper,
  LogoLoader,
  Modal,
  NewTableAdv,
  NoData,
  showToast,
  SmartTooltip,
  TableAdv,
  useFormHandler,
} from "../../../../components";
import { getEmployeeId, mapTableRows } from "../../../../utils";
import { formatCurrency } from "../../../../utils/stringUtils";
import axiosRequest from "../../../../redux/slices/universal/axiosService";
import { ENDPOINTS } from "../../../../redux/apiConfig";
import { EmploymentType } from "../../../../assets/data/enumData";
import dayjs from "dayjs";
import { usePagination } from "../../../../hooks";
import { downloadExcel, mapTableData } from "../../../../utils/spreadsheet";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setEmployeeProfileUiid } from "../../../../redux/slices/employee/employeeSlice";
import { getCurrentInvoiceStart } from "../../../../redux/slices/invoices/invoicesSlice";
import InvoiceDetails from "../../Invoices/Invoices/InvoiceDetails";
import {
  clearCurrentPayrollEditing,
} from "../../../../redux/slices/payroll/payrollSlice";
import ProposedEmployeesDetails from "./UpcomingPaydays/PayDayDetails/ProposedEmployeesDetails/ProposedEmployeesDetails";

const payrollStatusOptions = [
  { value: [1, 2, 3, 4], label: "All" },
  { value: [4], label: "Processed" },
  { value: [2, 3], label: "Pending" },
];
const payrollDataType = [
  {
    name: "Projection",
    color: "bg-yellow-300",
  },
  {
    name: "Finalized",
    color: "bg-blue-500/70",
  },
  {
    name: "Paid",
    color: "bg-green-400",
  },
];

const PayrollLegends = ({ visible }) => {
  if (!visible) return;
  return (
    <div className="tableLegends my-3 flex flex-wrap gap-x-4 gap-y-2 items-center justify-end">
      {payrollDataType?.map((item, index) => (
        <div key={index} className="flex items-center gap-1">
          <div
            className={`w-2.5 h-2.5 rounded-full ${item?.color}`}
          ></div>
          <span className="text-xs text-nowrap">{item?.name}</span>
        </div>
      )) || "" }
    </div>
  );
};
export function getRowBgClass(row) {
  if (!row) return "";

  if (row.status === 2) {
    return "bg-blue-100";
  }

  if (row.status === 4) {
    return "bg-green-100";
  }

  return "bg-yellow-100/80";
}

export function insertDateRangesAtIndex(arr, date) {
  if (date == null) return arr;
  const month = date.getMonth();
  const year = date.getFullYear();
  const startOfMonth = new Date(year, month, 1);
  const endOfMonth = new Date(year, month + 1, 0);
  const monthName = date.toLocaleString("en", { month: "short" });

  const ranges = [];
  let weekIndex = 1;
  let current = new Date(startOfMonth);

  // Handle first partial week if month doesn’t start on Monday
  if (current.getDay() !== 1) {
    const weekStart = new Date(current);
    const weekEnd = new Date(current);
    weekEnd.setDate(weekEnd.getDate() + (7 - current.getDay())); // up to Sunday
    if (weekEnd > endOfMonth) weekEnd.setDate(endOfMonth.getDate());

    ranges.push(
      ((weekNum) => ({
        key: `week${weekNum}`,
        label: `${monthName} ${weekStart.getDate()}-${weekEnd.getDate()}`,
        render: (row) => {
          if (!row[`week${weekNum}`]?.[`data`]?.timesheetId)
            return row[`week${weekNum}`]?.[`value`] || 0;
          return (
            <div className="flex items-center gap-1">
              <Link
                to="/employee/employee-profile/timesheet"
                state={{
                  uiid: row[`week${weekNum}`]?.[`data`]?.placementId || "",
                  timesheetId:
                    row[`week${weekNum}`]?.[`data`]?.timesheetId || "",
                  employeeId: row[`week${weekNum}`]?.[`data`]?.employeeId || "",
                }}
              >
                <span className="text-linkblue">
                  {row[`week${weekNum}`]?.[`value`] || 0}
                </span>
              </Link>
            </div>
          );
        },
      }))(weekIndex),
    );

    current = new Date(weekEnd);
    current.setDate(current.getDate() + 1);
    weekIndex++;
  }

  // Now process full Mon–Sun weeks
  while (current <= endOfMonth) {
    const weekStart = new Date(current);
    const weekEnd = new Date(current);
    weekEnd.setDate(weekEnd.getDate() + 6);
    if (weekEnd > endOfMonth) weekEnd.setDate(endOfMonth.getDate());
    ranges.push(
      ((weekNum) => ({
        key: `week${weekNum}`,
        label: `${monthName} ${weekStart.getDate()}${
          weekStart.getDate() === weekEnd.getDate()
            ? ""
            : "-" + weekEnd.getDate()
        }`,
        render: (row) => {
          if (!row[`week${weekNum}`]?.[`data`]?.timesheetId)
            return row[`week${weekNum}`]?.[`value`] || 0;
          return (
            <div className="flex items-center gap-1 ">
              <Link
                to="/employee/employee-profile/timesheet"
                state={{
                  uiid: row[`week${weekNum}`]?.[`data`]?.placementId || "",
                  timesheetId:
                    row[`week${weekNum}`]?.[`data`]?.timesheetId || "",
                  employeeId: row[`week${weekNum}`]?.[`data`]?.employeeId || "",
                }}
              >
                <span className="text-linkblue">
                  {row[`week${weekNum}`]?.[`value`] || 0}
                </span>
              </Link>
            </div>
          );
        },
      }))(weekIndex),
    );

    current.setDate(current.getDate() + 7);
    weekIndex++;
  }

  arr.splice(3, 0, ...ranges);
  return arr;
}

export function getWeeklyHours(dailyEntries = []) {
  if (!dailyEntries.length) return {};

  const firstDate = String(dailyEntries[0]?.date);
  const year = parseInt(firstDate.slice(0, 4));
  const month = parseInt(firstDate.slice(4, 6)) - 1;
  const startOfMonth = new Date(year, month, 1);
  const endOfMonth = new Date(year, month + 1, 0);

  const weeks = {};
  let weekIndex = 1;
  let current = new Date(startOfMonth);

  // Handle first partial week if month doesn’t start on Monday
  if (current.getDay() !== 1) {
    const weekStart = new Date(current);
    const weekEnd = new Date(current);
    weekEnd.setDate(weekEnd.getDate() + (7 - current.getDay()));
    if (weekEnd > endOfMonth) weekEnd.setDate(endOfMonth.getDate());

    const weekKey = `week${weekIndex}`;
    weeks[weekKey] = { value: 0, data: null };

    dailyEntries.forEach((entry) => {
      if (!entry || !entry.date) return;
      const str = String(entry.date);
      if (str.length !== 8) return;

      const y = parseInt(str.slice(0, 4));
      const m = parseInt(str.slice(4, 6)) - 1;
      const d = parseInt(str.slice(6, 8));
      const date = new Date(y, m, d);

      if (date >= weekStart && date <= weekEnd) {
        weeks[weekKey]["value"] += entry.st || 0;
        weeks[weekKey]["data"] = entry;
      }
    });

    current = new Date(weekEnd);
    current.setDate(current.getDate() + 1);
    weekIndex++;
  }

  // Process full Mon–Sun weeks
  while (current <= endOfMonth) {
    const weekStart = new Date(current);
    const weekEnd = new Date(current);
    weekEnd.setDate(weekEnd.getDate() + 6);
    if (weekEnd > endOfMonth) weekEnd.setDate(endOfMonth.getDate());

    const weekKey = `week${weekIndex}`;
    weeks[weekKey] = { value: 0, data: null };

    dailyEntries.forEach((entry) => {
      if (!entry || !entry.date) return;
      const str = String(entry.date);
      if (str.length !== 8) return;

      const y = parseInt(str.slice(0, 4));
      const m = parseInt(str.slice(4, 6)) - 1;
      const d = parseInt(str.slice(6, 8));
      const date = new Date(y, m, d);

      if (date >= weekStart && date <= weekEnd) {
        weeks[weekKey]["value"] += entry.st || 0;
        weeks[weekKey]["data"] = entry;
      }
    });

    current.setDate(current.getDate() + 7);
    weekIndex++;
  }
  return weeks;
}

export function buildColumns({
  dispatch,
  openPayrollDrawer,
  handleClickForInvoice,
}) {
  return [
    {
      key: "employeeID",
      label: "Consultant ID",
      getRowClass: (row) => getRowBgClass(row),
      columnMenu: false,
    },
    {
      key: "name",
      label: "Consultants Name",
      columnMenu: false,
      width: "min-w-[250px]! max-w-[300px]!",
      render: (row) => (
        <SmartTooltip
          description={row.name}
          size="xs"
          color="black"
          noicon
          tooltipPosition="bottom"
          portal={true}
        >
          <div className="flex items-center gap-1">
            <span
              className="cursor-pointer text-info line-clamp-1!"
              onClick={() => openPayrollDrawer(row.payrollId)}
            >
              {row.name}
            </span>
          </div>
        </SmartTooltip>
      ),
    },
    {
      key: "clientName",
      label: "Client Name",
      columnMenu: false,
      width: "min-w-[250px]! max-w-[300px]!",
      render: (row) => (
        <SmartTooltip
          description={row.clientName}
          size="xs"
          color="black"
          noicon
          tooltipPosition="bottom"
          portal={true}
        >
          <div className="flex items-center gap-1 line-clamp-1!">
            {row.clientName}
          </div>
        </SmartTooltip>
      ),
    },
    {
      key: "employeeType",
      label: "Employee Type",
      columnMenu: false,
    },
    {
      key: "total",
      label: "Total",
      columnMenu: false,
    },
    {
      key: "actualbillrate",
      label: "Bill Rate",
      columnMenu: false,
    },
    {
      key: "invoiceNumber",
      label: "Invoice Number",
      columnMenu: false,
      render: (row) => (
        <div className="flex flex-col gap-1">
          {row?.invoiceNumber.map((data, index) => (
            <div
              className="flex items-center gap-1"
              onClick={() => {
                handleClickForInvoice(data);
              }}
              key={index}
            >
              <span className="cursor-pointer text-linkblue">{data}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: "invoiceAmount",
      label: "Invoiced Amount",
      columnMenu: false,
      render: (row) => (
        <div className="flex items-center gap-1">
          {row.projected
            ? formatCurrency(row.actualbillrate * row.total)
            : row?.allPayrollInvoices?.length > 0
              ? row.invoiceAmount
              : formatCurrency(row.actualbillrate * row.total)}
        </div>
      ),
    },
    {
      key: "effectivebillrate",
      label: "Effective Bill Rate",
      columnMenu: false,
    },
    {
      key: "percentageSymbol",
      label: "%",
      columnMenu: false,
    },
    {
      key: "payRate",
      label: "Pay Rate",
      columnMenu: false,
    },
    {
      key: "payrollAmount",
      label: "Payroll Amount",
      columnMenu: false,
      render: (row) => (
        <div className="flex items-center gap-1">
          {row.projected
            ? formatCurrency(row.payRate * row.total)
            : row.payrollAmount}
        </div>
      ),
    },
    {
      key: "paidAmount",
      label: "Paid Amount",
      columnMenu: false,
    },
    {
      key: "pendingPayroll",
      label: "Pending Payroll",
      columnMenu: false,
    },
    {
      key: "deduction",
      label: "Deduction",
      columnMenu: false,
      render: (row) => (
        <div className="flex items-center gap-1">
          <Link
            to="/employee/employee-profile?payments=deductions&profile-info=payments"
            onClick={() => dispatch(setEmployeeProfileUiid(row.employeeUiid))}
          >
            {row.deduction}
          </Link>
        </div>
      ),
    },
    {
      key: "health",
      label: "Health",
      columnMenu: false,
      render: (row) => (
        <div className="flex items-center gap-1">
          <Link
            to="/employee/employee-profile?payments=deductions&profile-info=payments"
            onClick={() => dispatch(setEmployeeProfileUiid(row.employeeUiid))}
          >
            {row.health}
          </Link>
        </div>
      ),
    },
    {
      key: "secondProjectAmount",
      label: "2nd Project Amount",
      columnMenu: false,
    },
    {
      key: "otherPayment",
      label: "Other Payment",
      columnMenu: false,
      render: (row) => (
        <div className="flex items-center gap-1">
          <Link
            to="/employee/employee-profile?payments=other-payment&profile-info=payments"
            onClick={() => dispatch(setEmployeeProfileUiid(row.employeeUiid))}
          >
            {formatCurrency(
              row?.otherPayment?.payrollAmount
                ? row?.otherPayment?.payrollAmount
                : row?.otherPayment?.totalPay,
            )}
          </Link>
        </div>
      ),
    },
    {
      key: "referral",
      label: "Referral",
      columnMenu: false,
    },
    {
      key: "gpm",
      label: "GPM",
      columnMenu: false,
    },
    {
      key: "final",
      label: "Total Profit",
      columnMenu: false,
    },
    {
      key: "vmsCharge",
      label: "VMS Charge",
      columnMenu: false,
    },
  ];
}

export function computeMappedData(summary) {
  return (
    summary?.map((item) => {
      let totalHrs =
        item?.timesheet
          ?.flatMap((id) => id.dailyEntries || [])
          ?.reduce((acc, item) => acc + (item.st || 0), 0) || 0;

      let allEntries =
        item?.timesheet
          ?.flatMap((id) =>
            (id.dailyEntries || []).map((entry) => ({
              ...entry,
              timesheetId: id.uiid,
              placementId: item?.jobId?.uiid,
              employeeId: item?.employeeId,
            })),
          )
          ?.filter(
            (entry, index, self) =>
              entry &&
              entry.date &&
              index ===
                self.findIndex(
                  (e) => e.date === entry.date && e.id === entry.id,
                ),
          ) || [];

      const allPayrollInvoices =
        item?.timesheet?.flatMap((invoice) => invoice.invoiceId) || [];
      const allInvoicesNumbers = allPayrollInvoices?.map(
        (invoice) => invoice?.invoiceNum,
      );
      const allInvoicesTotal = allPayrollInvoices
        ?.flatMap((invoice) => invoice?.amounts)
        ?.reduce((acc, amount) => acc + amount?.invoiced, 0);
      const lastPayment = item?.payments?.[item?.payments.length - 1] || {};
      let totalrefrralAmount =
        lastPayment?.referral
          ?.map((empRef) => {
            let effective =
              Math.round(
                lastPayment?.billRate?.rate *
                  (1 - (lastPayment?.vmsCharges ?? 0) / 100) *
                  100,
              ) / 100;
            const calculateReferralAmount = empRef?.margin
              ? empRef?.referralAmount
              : empRef?.referralAmountType === 2
                ? effective * (empRef?.referralAmount / 100)
                : empRef?.referralAmountType === 1
                  ? empRef?.referralAmount
                  : empRef?.referralAmount;
            return calculateReferralAmount;
          })
          ?.reduce((a, b) => a + b, 0) || 0;
      let effectiveBillRate = lastPayment?.vmsCharges
        ? Math.round(
            lastPayment?.billRate?.rate *
              (1 - lastPayment?.vmsCharges / 100) *
              100,
          ) / 100 || 0
        : lastPayment?.billRate?.rate || 0;
      const gpm =
        (effectiveBillRate || 0) - (lastPayment?.payRate?.payRatePerHour || 0);
      const finalProfit = gpm * totalHrs;
      let { week1, week2, week3, week4, week5 } = getWeeklyHours(allEntries);
      return {
        payrollId: item?.uiid || null,
        status: item?.status || null,
        employeeUiid: item?.employeeId,
        employeeID: getEmployeeId(item?.employeeId, item?.employmentType),
        allEntries,
        name: item?.employeeName || "-",
        clientName: item?.clientName || "-",
        employeeType:
          EmploymentType.find((list) => list?.value === item?.employmentType)
            .label || "-",
        week1,
        week3,
        week2,
        week4,
        week5,
        total: Number(totalHrs).toFixed(2) || "0",
        actualbillrate: isNaN(lastPayment?.billRate?.rate)
          ? "-"
          : lastPayment?.billRate?.rate
            ? lastPayment?.billRate?.rate?.toFixed(2)
            : "-",
        invoiceNumber: allInvoicesNumbers,
        allPayrollInvoices,
        invoiceAmount: allInvoicesTotal
          ? formatCurrency(allInvoicesTotal)
          : "-",
        effectivebillrate: isNaN(effectiveBillRate)
          ? "-"
          : effectiveBillRate
            ? effectiveBillRate.toFixed(2)
            : "-" || 0,
        percentageSymbol: lastPayment?.payRate?.percent || "-",
        payRate: lastPayment?.payRate?.payRatePerHour || "-",
        payrollAmount: item?.amounts?.netPay
          ? formatCurrency(item?.amounts?.netPay)
          : 0 || 0,
        paidAmount: formatCurrency(item?.amounts?.paid) || 0,
        pendingPayroll: formatCurrency(
          item?.amounts?.netPay - (item?.amounts?.paid || 0),
        ),
        deduction:
          formatCurrency(item?.amounts?.diff - item?.amounts?.insurance) || "0",
        health: formatCurrency(item?.amounts?.insurance),
        secondProjectAmount: formatCurrency(item?.timesheets?.totalUnpaid),
        otherPayment: {
          payrollAmt: item?.otherPays?.payrollAmt || null,
          totalPay: item?.bonuses?.totalPay || null,
        },
        referral: formatCurrency(totalrefrralAmount) || "-",
        gpm: formatCurrency(gpm),
        final: formatCurrency(finalProfit),
        vmsCharge: lastPayment?.vmsCharges || "0",
        projected: item?.projected,
      };
    }) || []
  );
}

export const PayrollKpiView = ({
  isOpen,
  onClose,
  dateAndYear,
  paymentType,
  kpiType,
}) => {
  const dispatch = useDispatch();
  const [rowKpiData, setRowKpiData] = useState({});
  const [openKpiDrawer, setOpenKpiDrawer] = useState(false);

  const [summary, setSummary] = useState([]);
  const [mappedRows, setMappedRows] = useState([]);
  const [modifiedColumns, setModifiedColumns] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const { page, limit, setPage, handleLimitChange } = usePagination(10);

  const handleClickForInvoice = useCallback(
    (invoiceNum) => {
      if (!invoiceNum) return;
      dispatch(
        getCurrentInvoiceStart({
          filters: { invoiceNum: { type: "NONE", value: invoiceNum } },
          coll: "Invoice",
          ref: "Client Employee Placement Timesheet",
          incl: ["", "firstName lastName", "", "attachments"],
        }),
      );
      setSelectedInvoice(invoiceNum);
      // you might want to show invoice details here, reuse parent state if needed
    },
    [dispatch],
  );

  const openPayrollDrawer = useCallback(
    async (id) => {
      try {
        setLoading(true);
        const response = await axiosRequest({
          method: "GET",
          url: ENDPOINTS.PAYROLL.GET_BY_UIID,
          params: {
            coll: "Payroll",
            ref: "Employee SalProfile PayConfig",
            fil: JSON.stringify({
              uiid: {
                type: "NONE",
                value: id,
              },
            }),
          },
        });
        setRowKpiData(response?.data?.data || {});
        setOpenKpiDrawer(true);
      } catch (error) {
        console.error("error in preview", error);
        showToast("Unable to load payroll details", "error", 5000);
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  const columns = useCallback(
    () => buildColumns({ dispatch, openPayrollDrawer, handleClickForInvoice }),
    [dispatch, openPayrollDrawer, handleClickForInvoice],
  );

  const fetchSummary = useCallback(async () => {
    if (!dateAndYear) return;
    console.info("fetchSummary:::::::::::::", kpiType);
    let status = null;
    switch (kpiType) {
      case "Total candidates":
        status = [1, 2, 3, 4];
        break;
      case "Processed Payroll":
        status = [4];
        break;
      case "Pending Payroll ":
        status = [2, 3];
        break;
      case "Total amount":
        status = [1, 2, 3, 4];
        break;
      case "Amount paid":
        status = [4];
        break;
      case "Pending Payroll":
        status = [2, 3];
        break;
      default:
        throw new Error("Invalid kpiType");
    }
    try {
      setLoading(true);
      const params = {
        coll: "Payroll",
        lim: limit,
        pg: page,
        ret: "employeeId jobId timesheets amounts status payCycle otherPays",
        ref: "Employee PlacementWithClient TimesheetWithInvoice InsuranceDed",
        fil: JSON.stringify({
          payCycle: {
            type: "RANGE",
            value: {
              from: dayjs(dateAndYear).startOf("month").format("YYYY-MM-DD"),
              to: dayjs(dateAndYear).endOf("month").format("YYYY-MM-DD"),
            },
          },
          status: {
            type: "IN",
            // value: paymentType,
            value: status,
          },
        }),
        sort: JSON.stringify({ employeeId: 1 }),
        incl: [
          "firstName lastName employmentType",
          "payments.payRate.payRatePerHour payments.billRate.rate payments.referral payments.vmsCharges payments.payRate.percent",
          "dailyEntries",
        ],
      };

      const [response] = await Promise.all([
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.PAYROLL.GET_ALL,
          params: {
            coll: "Payroll",
            useAggregate: true,
            lim: limit,
            pg: page,
            fil: JSON.stringify({
              payCycle: {
                type: "AG_PAYROLL_SUMMARY",
                value: {
                  from: dayjs(dateAndYear)
                    .startOf("month")
                    .format("YYYY-MM-DD"),
                  to: dayjs(dateAndYear).endOf("month").format("YYYY-MM-DD"),
                  // status: paymentType,
                  status: status,
                  search: searchValue,
                },
              },
            }),
          },
        }),
      ]);
      setTotalCount(response?.data?.data?.[0]?.total?.[0]?.count || 0);
      setSummary(response?.data?.data?.[0]?.data || []);
    } catch (error) {
      console.error("error fetching KPI summary", error);
    } finally {
      setLoading(false);
    }
  }, [dateAndYear, paymentType, limit, page, kpiType, searchValue]);

  // clear when modal closed
  useEffect(() => {
    if (!isOpen) {
      setSummary([]);
      setMappedRows([]);
      setModifiedColumns([]);
      setTotalCount(0);
      setPage(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchSummary();
    }
  }, [isOpen, dateAndYear, paymentType, limit, page, fetchSummary]);

  useEffect(() => {
    const cols = insertDateRangesAtIndex(columns(), dateAndYear);
    setModifiedColumns((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(cols)) return prev;
      return cols;
    });

    const newRows = mapTableRows(computeMappedData(summary), cols);
    setMappedRows((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(newRows)) return prev;
      return newRows;
    });
  }, [summary, dateAndYear, page, limit, columns]);

  return (
    <div>
      <div className="overflow-hidden flex flex-1">
        <TableAdv
          tableId={"payroll_summary_kpiclicktable"}
          showSlNo
          serverPagination={true}
          columns={modifiedColumns}
          rows={mappedRows}
          // fixedCol={1}
          totalCount={totalCount}
          page={page}
          limit={limit}
          onPaginationChange={setPage}
          onLimitChange={handleLimitChange}
          serverSearchValue={searchValue}
          serverOnSearch={(key, val) => {
            setSearchValue(val);
            setPage(1);
          }}
          toolbarRight={{
            search: true,
            filter:true,
            add: [
              {
                component:  <PayrollLegends visible={true} />
              }
            ]
          }}
        />
      </div>
      <Drawer
        isOpen={openKpiDrawer}
        onClose={() => {
          setOpenKpiDrawer(false);
          setRowKpiData({});
          dispatch(clearCurrentPayrollEditing());
        }}
        position="left"
        width="w-[80%]"
        title=""
        closeOnOutsideClick
        showCloseButton={false}
        className="!p-0"
        bodyGap={false}
      >
        <div>
          <ProposedEmployeesDetails
            hideButtons={true}
            disableEditing={true}
            onHoldClick={() => {
              setOpenKpiDrawer(false);
              setRowKpiData({});
            }}
            rowData={rowKpiData}
          />
        </div>
      </Drawer>
      <Modal
        isOpen={selectedInvoice}
        title={"Invoice Details"}
        onClose={() => setSelectedInvoice(null)}
        showCloseButton={true}
        closeOnOutsideClick={true}
        width="max-w-full"
      >
        <InvoiceDetails
          invoice={selectedInvoice}
          onBack={() => setSelectedInvoice(null)}
          removeButtonForPreview={true}
        />
      </Modal>
    </div>
  );
};

const Summary = (props) => {
  const dispatch = useDispatch();
  const [kpiModalOpen, setKpiModalOpen] = useState(false);
  const [kpiType, setKpiType] = useState(null);
  const [summary, setSummary] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [kpiMetrics, setKPIMetrics] = useState([]);
  const [mappedRows, setMappedRows] = useState([]);
  const [modifiedColumns, setModifiedColumns] = useState([]);
  const [totalCount, setTotalCount] = useState(100);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const [rowData, setRowData] = useState({});
  const [openDrawer, setOpenDrawer] = useState(false);

  const { formData, errors, handleChange, validateAllFields } = useFormHandler(
    {
      dateAndYear: dayjs().subtract(1, "month").toDate(),
      paymentType: [1, 2, 3, 4],
    },
    {},
  );

  const handleClickForInvoice = useCallback(
    (invoiceNum) => {
      if (!invoiceNum) return;
      dispatch(
        getCurrentInvoiceStart({
          filters: { invoiceNum: { type: "NONE", value: invoiceNum } },
          coll: "Invoice",
          ref: "Client Employee Placement Timesheet",
          incl: ["", "firstName lastName", "", "attachments"],
        }),
      );
      setSelectedInvoice(invoiceNum);
    },
    [dispatch],
  );

  const openPayrollDrawer = useCallback(
    async (id) => {
      try {
        setLoading(true);
        const response = await axiosRequest({
          method: "GET",
          url: ENDPOINTS.PAYROLL.GET_BY_UIID,
          params: {
            coll: "Payroll",
            ref: "Employee SalProfile PayConfig",
            fil: JSON.stringify({
              uiid: {
                type: "NONE",
                value: id,
              },
            }),
          },
        });
        setRowData(response?.data?.data || {});
        setOpenDrawer(true);
      } catch (error) {
        console.error("error in preview", error);
        showToast("Unable to load payroll details", "error", 5000);
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  const columns = useMemo(
    () => buildColumns({ dispatch, openPayrollDrawer, handleClickForInvoice }),
    [dispatch, openPayrollDrawer, handleClickForInvoice],
  );

  const KPIColumns = [
    {
      title: "Total candidates",
      variant: "contained",
      // color: "success",

      value: kpiMetrics?.[0]?.totalCandidates || 0,
    },
    {
      title: "Processed Payroll",
      variant: "contained",
      value: kpiMetrics?.[0]?.processedPayments?.candidate || 0,
      // color: "success",
    },
    {
      title: "Pending Payroll",
      variant: "contained",
      value: kpiMetrics?.[0]?.pendingPayments?.candidate || 0,
    },
    {
      title: "Total amount",
      variant: "contained",
      value: formatCurrency(kpiMetrics?.[0]?.totalAmount),
    },
    {
      title: "Amount paid",
      variant: "contained",
      value: formatCurrency(kpiMetrics?.[0]?.processedPayments?.amount),
    },
    {
      title: "Pending Payroll",
      variant: "contained",
      value: formatCurrency(kpiMetrics?.[0]?.amountDue),
    },
  ];

  const { page, limit, setPage, handleLimitChange } = usePagination(10);

  const fetchSummary = async () => {
    if (!formData.dateAndYear) return;
    try {
      setLoading(true);
      const params = {
        coll: "Payroll",
        lim: limit,
        pg: page,
        ret: "employeeId jobId timesheets amounts status payCycle otherPays",
        //   ref: "Employee PlacementWithClient Timesheet InsuranceDed",
        ref: "Employee PlacementWithClient TimesheetWithInvoice InsuranceDed",
        fil: JSON.stringify({
          payCycle: {
            type: "RANGE",
            value: {
              from: dayjs(formData.dateAndYear)
                .startOf("month")
                .format("YYYY-MM-DD"),
              to: dayjs(formData.dateAndYear)
                .endOf("month")
                .format("YYYY-MM-DD"),
            },
          },
          status: {
            type: "IN",
            value: formData.paymentType,
          },
        }),
        sort: JSON.stringify({ employeeId: 1 }),
        incl: [
          "firstName lastName employmentType",
          "payments.payRate.payRatePerHour payments.billRate.rate payments.referral payments.vmsCharges payments.payRate.percent",
          "dailyEntries",
        ],
      };

      const [response, kpiMetrics] = await Promise.all([
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.PAYROLL.GET_ALL,
          params: {
            coll: "Payroll",
            useAggregate: true,
            lim: limit,
            pg: page,
            fil: JSON.stringify({
              payCycle: {
                type: "AG_PAYROLL_SUMMARY",
                value: {
                  from: dayjs(formData.dateAndYear)
                    .startOf("month")
                    .format("YYYY-MM-DD"),
                  to: dayjs(formData.dateAndYear)
                    .endOf("month")
                    .format("YYYY-MM-DD"),
                  status: formData.paymentType,
                  search: searchValue,
                },
              },
            }),
          },
        }),
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.PAYROLL.GET_ALL,
          params: {
            coll: "Payroll",
            useAggregate: true,
            // fil: JSON.stringify({
            //   payCycle: {
            //     type: "AG_PAYROLL_KPI",
            //     value: {
            //       from: dayjs(formData.dateAndYear)
            //         .startOf("month")
            //         .format("YYYY-MM-DD"),
            //       to: dayjs(formData.dateAndYear)
            //         .endOf("month")
            //         .format("YYYY-MM-DD"),
            //       status: formData.paymentType,
            //     },
            //   },
            // }),

            fil: JSON.stringify({
              payrollkpi: {
                type: "AG_STATS",
                value: [
                  dayjs(formData.dateAndYear)
                    .startOf("month")
                    .format("YYYY-MM-DD"),
                  dayjs(formData.dateAndYear)
                    .endOf("month")
                    .format("YYYY-MM-DD"),
                  formData.paymentType,
                ],
              },
            }),
          },
        }),
      ]);
      setKPIMetrics(kpiMetrics?.data?.data || []);
      setTotalCount(response?.data?.data?.[0]?.total?.[0]?.count || 0);
      setSummary(response?.data?.data?.[0]?.data || []);
      if (response.errorCode === 404) {
        setLoading(false);

        // showToast("Payrolls Not found", "error", 5000);
      }
    } catch (error) {
      setLoading(true);

      console.error("error block in summary", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!formData.dateAndYear) {
      setSummary([]);
      setKPIMetrics([]);
      setTotalCount(0);
    } else {
      fetchSummary();
    }
  }, [formData.dateAndYear, formData.paymentType, limit, page, searchValue]);

  useEffect(() => {
    const currentSummary = formData.dateAndYear;
    const cols = insertDateRangesAtIndex(columns, currentSummary);
    setModifiedColumns((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(cols)) return prev;
      return cols;
    });

    const newRows = mapTableRows(computeMappedData(summary), cols);
    setMappedRows((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(newRows)) return prev;
      return newRows;
    });
  }, [
    formData.dateAndYear,
    formData.paymentType,
    limit,
    page,
    summary,
    columns,
  ]);

  const downloadExcelFunc = (
    tableData,
    name,
    date,
    dateFormat = "MMMM",
    sheetName,
  ) => {
    try {
      setLoading(true);
      downloadExcel(tableData, name, date, dateFormat, sheetName);
    } catch (error) {
      console.error("downloadExcelFunc", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {" "}
      <Drawer
        isOpen={openDrawer}
        onClose={() => {
          setOpenDrawer(false);
          setRowData({});
          dispatch(clearCurrentPayrollEditing());
        }}
        position="left" // 'left', 'right', 'top', 'bottom'
        width="w-[80%]" // custom width using Tailwind classes
        title=""
        closeOnOutsideClick
        showCloseButton={false}
        className="!p-0"
        bodyGap={false}
        hiden
      >
        <div>
          <ProposedEmployeesDetails
            hideButtons={true}
            disableEditing={true}
            onHoldClick={() => {
              setOpenDrawer(false);
              setRowData({});
            }}
            rowData={rowData}
            kpiLoading={loading}
          />
        </div>
      </Drawer>
      <div></div>
      <div className="mt-4 mb-4">
        <InfoWrapper className={"min-w-[250px]"}>
          {KPIColumns?.map((item, index) => (
            <InfoCardNewAdv
              layout="option13"
              key={index}
              title={item?.title}
              variant={item?.variant}
              value={item?.value}
              valueConfig={{ className: "text-[28px]!" }}
              onClick={() => {
                setKpiModalOpen(true);
                setKpiType(item?.title);
              }}
              isLoading={loading}
            />
          ))}
        </InfoWrapper>
      </div>
      <div className="overflow-hidden flex flex-1">
        <TableAdv
          tableId={"payroll_payrollsummary"}
          showSlNo
          rows={mappedRows}
          columns={modifiedColumns}
          serverPagination={true}
          totalCount={totalCount}
          page={page}
          limit={limit}
          onPaginationChange={setPage}
          onLimitChange={handleLimitChange}
          serverSearchValue={searchValue}
          serverOnSearch={(key, val) => {
            setSearchValue(val);
            setPage(1);
          }}
          toolbarRight={{
            filter: true,
            search: true,
            add: [
              {
                component: (
                  <div className="flex  items-center gap-2 justify-center">
                    <PayrollLegends visible={true} />
                    <DropdownMenu
                      MenuSection={
                        <div className="flex flex-col">
                          <Button
                            title={"EXCEL"}
                            variant="text"
                            color="black"
                            size="xs-sm"
                            className="!justify-start py-1"
                            onClick={() =>
                              downloadExcelFunc(
                                mapTableData(mappedRows, modifiedColumns),
                                `Payroll_Summary_${
                                  payrollStatusOptions?.find(
                                    (item) =>
                                      item.value.length ===
                                        formData.paymentType.length &&
                                      item.value.every((v) =>
                                        formData.paymentType.includes(v),
                                      ),
                                  )?.label || ""
                                }`,
                                formData.dateAndYear,
                                "MMMM",
                                "Payroll",
                              )
                            }
                          />
                        </div>
                      }
                    >
                      <Button
                        color="black"
                        title=""
                        size="md"
                        icon={"screen_share"}
                        variant="text"
                      />
                    </DropdownMenu>
                    <div className="flex  items-center gap-4 justify-center">
                      <SmartTooltip
                        description={"Refresh"}
                        size="xs"
                        color="black"
                        noicon
                        tooltipPosition="top"
                        portal={true}
                      >
                        <Button
                          size="md"
                          noTitle
                          variant="text"
                          icon={"refresh"}
                          color="black"
                          className="px-0! bg-transparent!"
                          onClick={fetchSummary}
                        />
                      </SmartTooltip>
                      <div className="min-w-[150px]">
                        <FormFieldSet
                          type="monthpicker"
                          // title={""}
                          noTitle
                          size="sm"
                          placeholder="Select Month & Year"
                          value={formData.dateAndYear}
                          onChange={(value) =>
                            handleChange("dateAndYear", value)
                          }
                        />
                      </div>
                      <div className="min-w-[150px]">
                        <FormFieldSet
                          size="sm"
                          type="select"
                          // title={""}
                          noTitle
                          placeholder="Select Payment Status"
                          value={formData.paymentType}
                          onChange={(value) =>
                            handleChange("paymentType", value)
                          }
                          options={payrollStatusOptions}
                        />
                      </div>
                    </div>
                  </div>
                ),
              },
            ],
          }}
        />
      </div>
      <Modal
        isOpen={kpiModalOpen}
        title={`Payroll - ${kpiType || ""}`}
        onClose={() => {
          setKpiModalOpen(false);
          setKpiType(null);
        }}
        showCloseButton={true}
        closeOnOutsideClick={true}
        width="max-w-[90vw]"
        height="h-[90vh]"
      >
        <PayrollKpiView
          isOpen={kpiModalOpen}
          onClose={() => {
            setKpiModalOpen(false);
            setKpiType(null);
          }}
          dateAndYear={formData.dateAndYear}
          paymentType={formData.paymentType}
          kpiType={kpiType}
        />
      </Modal>
      <Modal
        isOpen={selectedInvoice}
        title={"Invoice Details"}
        onClose={() => setSelectedInvoice(null)}
        showCloseButton={true}
        closeOnOutsideClick={true}
        width="max-w-full"
      >
        <InvoiceDetails
          invoice={selectedInvoice}
          onBack={() => setSelectedInvoice(null)}
          removeButtonForPreview={true}
        />
      </Modal>
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default Summary;
